import { useState, type ReactNode } from 'react';
import type { ItemSpec } from '../../engine';
import {
  applyBenchCurio,
  curioSlots,
  mantelSlots,
  mantelUsed,
  type BenchTarget,
  type GainTarget,
  type NightRun,
  type Offer,
  type Profile,
} from '../../campaign';
import { t } from '../../i18n';
import { itemName, itemText } from '../../app/describe';
import { Modal } from './Modal';
import { DeckPicker, TargetPicker, affords, targetNeed } from './NightParts';
import { BrassPill } from './night/Item';
import { NightIcon } from './night/NightIcon';

/**
 * What the Long Night's scenes share (docs/SHOP_DESIGN.md §7, §13): taking an offer (with its
 * target and payment), why one can't be taken, a plain screen, and the bench for Bench Curios.
 */

export type Apply = (fn: (p: Profile) => Profile) => void;

/** An offer waiting for its target (and payment) before it is taken. */
interface Pending {
  readonly offer: Offer;
  readonly payPawns: number;
  readonly take: (target: GainTarget, payWith: string[]) => (p: Profile) => Profile;
}

/** Why an offer can't be taken now (brass, no room on the Mantel or in the pockets), or null. */
export function blocked(run: NightRun, o: Offer, price = 0): string | null {
  if (price > 0 && !affords(run, price)) return t('night.why.brass');
  if (o.kind === 'superstition' && o.finish !== 'ghost' && mantelUsed(run) >= mantelSlots(run))
    return t('night.why.mantel');
  if (o.kind === 'curio' && run.curios.length >= curioSlots(run)) return t('night.why.pockets');
  return null;
}

/** Takes an offer at once, or asks what it needs first. */
export function useTaker(apply: Apply) {
  const [pending, setPending] = useState<Pending | null>(null);
  const take = (
    offer: Offer,
    fn: (target: GainTarget, payWith: string[]) => (p: Profile) => Profile,
    payPawns = 0,
  ) => {
    if (targetNeed(offer) === null && payPawns === 0) apply(fn({}, []));
    else setPending({ offer, payPawns, take: fn });
  };
  return { pending, setPending, take };
}

export function PendingPicker({
  profile,
  run,
  pending,
  apply,
  onClose,
}: {
  profile: Profile;
  run: NightRun;
  pending: Pending | null;
  apply: Apply;
  onClose: () => void;
}) {
  if (!pending) return null;
  return (
    <TargetPicker
      profile={profile}
      run={run}
      offer={pending.offer}
      payPawns={pending.payPawns}
      onCancel={onClose}
      onDone={(target, payWith) => {
        onClose();
        apply(pending.take(target, payWith));
      }}
    />
  );
}

/** A screen of the run: a title, a line saying what to do, brass, and the content. */
export function NightScreen({
  eyebrow,
  title,
  lead,
  brass,
  onInventory,
  children,
  dock,
}: {
  eyebrow?: string;
  title: string;
  lead?: string;
  brass?: number;
  onInventory?: () => void;
  children: ReactNode;
  /** The main action, fixed at the bottom of the screen. */
  dock?: ReactNode;
}) {
  return (
    <div className={`ln-screen ${dock ? 'has-dock' : ''}`}>
      <header className="ln-screen-head">
        <div className="ln-screen-titles">
          {eyebrow && <p className="ln-eyebrow">{eyebrow}</p>}
          <h2 className="ln-title">{title}</h2>
        </div>
        <div className="ln-head-side">
          {brass !== undefined && <BrassPill brass={brass} large />}
          {onInventory && (
            <button
              type="button"
              className="ln-icon-btn"
              onClick={onInventory}
              aria-label={t('night.inventory')}
              title={t('night.inventory')}
            >
              <NightIcon name="ledger" size={20} />
            </button>
          )}
        </div>
      </header>
      {lead && <p className="ln-lead">{lead}</p>}
      {children}
      {dock && <div className="ln-dock">{dock}</div>}
    </div>
  );
}

/** A section: a small heading (and something beside it), a hint, and its content. */
export function Section({
  title,
  hint,
  side,
  children,
}: {
  title: string;
  hint?: string;
  side?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="ln-section">
      <div className="ln-section-head">
        <h3 className="ln-eyebrow">{title}</h3>
        {side}
      </div>
      {hint && <p className="ln-hint">{hint}</p>}
      {children}
    </section>
  );
}

/** Bench Curios, used between tables on the run deck (docs/SHOP_DESIGN.md §10.4). */
export function BenchDialog({
  run,
  item,
  apply,
  onClose,
}: {
  run: NightRun;
  item: ItemSpec;
  apply: Apply;
  onClose: () => void;
}) {
  const [cards, setCards] = useState<number[]>([]);
  const [source, setSource] = useState<number | null>(null);
  const [up, setUp] = useState(true);
  const id = item.id;
  const max = id === 'file' || id === 'tracing-paper' ? 1 : 2;
  const needsCards = id !== 'coin-purse' && id !== 'appraisers-note';
  const ready =
    !needsCards ||
    (cards.length > 0 && (id !== 'ink-pot' || (source !== null && !cards.includes(source))));
  const target: BenchTarget = {
    cards,
    ...(source !== null ? { source } : {}),
    ...(id === 'file' ? { up } : {}),
  };
  return (
    <Modal
      title={itemName(id)}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            {t('action.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!ready}
            onClick={() => {
              onClose();
              apply((p) => applyBenchCurio(p, item.uid, target));
            }}
          >
            {t('night.use')}
          </button>
        </>
      }
    >
      <p className="ln-sheet-text">{itemText(id)}</p>
      {id === 'ink-pot' && (
        <>
          <p className="ln-eyebrow">{t('night.bench.source')}</p>
          <DeckPicker
            deck={run.deck}
            max={1}
            selected={source === null ? [] : [source]}
            onChange={(sel) => setSource(sel[0] ?? null)}
            filter={(c) => c.kind === 'normal'}
          />
        </>
      )}
      {id === 'file' && (
        <div className="ln-choice">
          <button
            type="button"
            className={`btn btn-small ${up ? 'btn-primary' : ''}`}
            onClick={() => setUp(true)}
          >
            {t('night.bench.up')}
          </button>
          <button
            type="button"
            className={`btn btn-small ${!up ? 'btn-primary' : ''}`}
            onClick={() => setUp(false)}
          >
            {t('night.bench.down')}
          </button>
        </div>
      )}
      {needsCards && (
        <>
          <p className="ln-eyebrow">{t('night.bench.cards', { n: max })}</p>
          <DeckPicker deck={run.deck} max={max} selected={cards} onChange={setCards} />
        </>
      )}
    </Modal>
  );
}
